// The Floor (docs/cockpit/spec.md): one card per switched-on Value Stream, its header counting the live Runs,
// those that need you and those running late, and naming the Value Driver it moves. Inside, the Value Stream's
// steps left to right, each Run on the step it stands on; a Run opens its Run panel. Amber outlines only the
// Runs waiting on you; late is red-orange. Each step is at least a readable width and the steps wrap onto rows, so
// the page never scrolls sideways (#346); they stack on phones.
import { useEffect, useId, useState } from "react";
import type { Api, FloorCard, FloorRun } from "../api.js";
import { useWords } from "../i18n/words.js";
import { Icon } from "../shell/icons.js";

function RunTicket({ run, onOpenRun }: { run: FloorRun; onOpenRun: (run: string) => void }) {
  const w = useWords();
  return (
    <li>
      <button type="button" className={run.needsYou ? "ticket need" : "ticket"} onClick={() => onOpenRun(run.run)}>
        <b>{run.title}</b>
        {run.waitingOn ? <span className="small">{run.waitingOn}</span> : null}
        {run.late ? <span className="small late">{w.floorLate}</span> : null}
        {run.needsYou ? <span className="tag tag-need small">{w.floorNeedsYou}</span> : null}
      </button>
    </li>
  );
}

/**
 * A step's short plain name: its name up to the first aside, a bracket, semicolon or colon ("Multi-channel capture
 * (walk-in, call, ...)" reads "Multi-channel capture"). The whole name when there is no aside.
 */
export function shortName(name: string): string {
  const cut = name.search(/\s*[(;:]/);
  const short = cut > 0 ? name.slice(0, cut).trim() : "";
  return short === "" ? name : short;
}

/** Past this many characters a name may be cut at three lines, so its whole name is on hand. */
const LONG = 40;

/** A step's short name, with its whole name on hover or keyboard focus when that says more. */
function StepName({ name }: { name: string }) {
  const id = useId();
  const short = shortName(name);
  if (short === name && name.length <= LONG) return <>{name}</>;
  return (
    <span className="step-name" tabIndex={0} aria-describedby={id}>
      {short}
      <span className="step-full" role="tooltip" id={id}>
        {name}
      </span>
    </span>
  );
}

function Card({ card, onOpenRun }: { card: FloorCard; onOpenRun: (run: string) => void }) {
  const w = useWords();
  return (
    <section className="lane" aria-label={w.floorStream(card.name)}>
      <header>
        <div className="lane-name">
          <Icon name="floor" />
          <h2>{w.floorStream(card.name)}</h2>
        </div>
        <div className="lane-meta">
          <span className="tag tag-plain">{w.floorLive(card.live)}</span>
          {card.needYou > 0 ? <span className="tag tag-need">{w.floorNeedYou(card.needYou)}</span> : null}
          {card.late > 0 ? <span className="tag tag-bad">{w.floorLateCount(card.late)}</span> : null}
          {card.driver ? <span className="small muted">{w.floorMoves(card.driver)}</span> : null}
        </div>
      </header>
      <div className="stages">
        {card.steps.map((step) => (
          <div className="stage" key={step.step}>
            <h3>
              <StepName name={step.name} /> ({step.runs.length})
            </h3>
            {step.runs.length > 0 ? (
              <ul className="tickets">
                {step.runs.map((run) => (
                  <RunTicket key={run.run} run={run} onOpenRun={onOpenRun} />
                ))}
              </ul>
            ) : (
              <p className="empty">{w.floorNothing}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export function Floor({ api, onOpenRun }: { api: Pick<Api, "floor">; onOpenRun: (run: string) => void }) {
  const w = useWords();
  const [cards, setCards] = useState<readonly FloorCard[] | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.floor().then(setCards, () => setFailed(true));
  }, [api]);

  if (!cards) return failed ? <p role="alert">{w.floorFailed}</p> : <p role="status">{w.floorLoading}</p>;
  return (
    <>
      <div className="hello">
        <h2>{w.floorTitle}</h2>
        <p className="muted">{w.floorIntro}</p>
      </div>
      {cards.length === 0 ? (
        <p className="muted">{w.floorNone}</p>
      ) : (
        cards.map((card) => <Card key={card.valueStream} card={card} onOpenRun={onOpenRun} />)
      )}
    </>
  );
}
